import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import Button from "../../../ui-lib/components/Button/Button";
import { Row, Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import {
  ObjectActivationData,
  ObjectDetailsModel,
  ObjectPatch,
} from "../../../core/api/objects/types";

import useTranslations from "../../../core/i18n/useTranslations";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import {
  activateSubscription,
  patchUpdateObject,
} from "../../../core/api/objects/objects";
import useUser from "../../../core/user/useUser";
import Divider from "../../../ui-lib/components/Divider/Divider";

import { IDropdownItem } from "../../../ui-lib/components/Dropdown/DropdownItem";

import {
  useObjectForm,
  useObjectFormRef,
} from "../../../core/SaveObjectContext/hooks";
import useSharedObject from "../SharedObjectContext/useSharedObject";
import { getProducts } from "../../../core/api/products/products";
import { IProductCategory } from "../../../core/api/products/types";
import { Role, RoleFeature } from "../../../components/RoleFeature";
import { OrganisationSubscription } from "../../../core/api/subscriptions";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import { CancelSubscriptionModal } from "./CancelSubscriptionModal";
import { ActivationCodeModal } from "./ActivationCodeModal";

const ObjectSubscriptionForm = ({
  objectId,
  objectData,
  subscriptionData,
  fetchObjectSubscription,
  subscriptionUpdateInProgress,
  onSubmit,
  ARCs,
}: {
  objectId: string;
  objectData: ObjectDetailsModel;
  subscriptionData: ObjectActivationData;
  fetchObjectSubscription: () => Promise<void>;
  subscriptionUpdateInProgress: any;
  onSubmit: (values: any) => Promise<void>;
  ARCs: OrganisationSubscription[];
}) => {
  const t = useTranslations();
  const { fetchObject } = useSharedObject();
  const { authenticatedRequest, config } = useUser();
  const [categories, setCategories] = useState<IDropdownItem[]>();

  const [selectedSubscription, setSelectedSubscription] =
    useState<IDropdownItem>({
      name: subscriptionData.subscription,
      id: subscriptionData.subscriptionId,
      isSelected: true,
    });

  const canActivateSubscription = config?.show.includes("UserActivate");

  const formRef = useObjectFormRef();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useObjectForm<ObjectDetailsModel>({
    defaultValues: objectData,
  });

  const fetchCategories = async () => {
    try {
      const response = await getProducts(authenticatedRequest);
      setCategories(
        response.data.map((r: IProductCategory) => ({
          id: r.productCategoryId,
          name: t(`Common:product_categories_${r.name}`),
          isSelected: false,
        }))
      );
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  useEffect(() => {
    if (!categories) {
      fetchCategories();
    }
  }, []);

  const isActivateButtonDisabled = () => {
    if (!selectedSubscription.id) {
      return true;
    }
    return !canActivateSubscription;
  };

  const renderSubscriptionActionButton = (id: string) =>
    subscriptionData.subscriptionActive ? (
      <CancelSubscriptionModal
        objectId={objectId}
        fetchObjectSubscription={fetchObjectSubscription}
      />
    ) : (
      <Button
        text={t("Common:labels_activate_subscription")}
        disabled={isActivateButtonDisabled()}
        dataCy="activate_subscription_btn"
        onClick={async () => {
          subscriptionUpdateInProgress(true);
          try {
            await activateSubscription(
              id,
              authenticatedRequest,
              undefined,
              +selectedSubscription.id
            );
            await fetchObject();
            await fetchObjectSubscription();
          } catch (error: any) {
            notifyApiErrors(error.response?.data?.errors);
          } finally {
            subscriptionUpdateInProgress(false);
          }
        }}
      />
    );

  return (
    <>
      <TwoColGrid>
        {subscriptionData.subscriptionActive ? (
          <TextInput
            dataCy="objectSubscription"
            value={subscriptionData.subscription}
            label={t("Objects:subscription")}
            disabled
          />
        ) : (
          <Dropdown
            withFilter
            maxVisible={8}
            dataCy="objectSubscriptionDropDown"
            title={t("Objects:subscription")}
            onSelectItem={(item) => {
              setSelectedSubscription(item);
            }}
            items={ARCs.map((arc) => ({
              id: arc.subscriptionId,
              name: arc.subscriptionGroupDescription,
              isSelected: selectedSubscription.id === arc.subscriptionId,
            }))}
            selectedItem={{
              ...selectedSubscription,
              isSelected: true,
            }}
            disabled={!canActivateSubscription}
          />
        )}
      </TwoColGrid>
      <RoleFeature requires={[Role.ActivateSubscriptions]}>
        <Spacer size={32} />
        <Row align="start" type="left">
          {renderSubscriptionActionButton(objectId)}
          <Spacer size={8} />
          <ActivationCodeModal
            objectId={objectId}
            subscriptionData={subscriptionData}
            fetchObjectSubscription={fetchObjectSubscription}
          />
          <RoleFeature
            type="everyNot"
            requires={[Role.ViserStandart, Role.ViserHTJ]}
          >
            <Spacer size={8} />
            <Button
              image="beaker"
              text={
                !objectData.demoAccount
                  ? t("Common:labels_set_demo_object")
                  : t("Common:labels_unset_demo_object")
              }
              variant="secondary"
              iconVariant={!objectData.demoAccount ? "secondary" : "link"}
              onClick={async () => {
                subscriptionUpdateInProgress(true);
                try {
                  await patchUpdateObject(
                    objectId,
                    {
                      demoAccount: !objectData.demoAccount,
                    } as ObjectPatch,
                    authenticatedRequest
                  );
                  await fetchObject();
                } catch (error: any) {
                  notifyApiErrors(error.response?.data?.errors);
                } finally {
                  subscriptionUpdateInProgress(false);
                }
              }}
            />
          </RoleFeature>
        </Row>
      </RoleFeature>
      {config!.show.includes("ChangeObjectAlarmType") && (
        <>
          <Spacer size={32} />
          <TwoColGrid>
            <Controller
              name="productCategoryId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Dropdown
                  required
                  title={t("Objects:category")}
                  id="addobject_category"
                  items={categories ?? []}
                  disabled={!categories?.length}
                  placeholder={t("Objects:selectcategory")}
                  selectedItem={categories?.find(
                    (r) => getValues(field.name) === r.id
                  )}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                  }}
                  invalid={errors.productCategoryId !== undefined}
                  validationError={
                    errors.productCategoryId &&
                    t("Objects:Validation_productcategory")
                  }
                />
              )}
            />
          </TwoColGrid>
        </>
      )}
      <Spacer size={32} />
      <Divider />
      <Spacer size={32} />
      <form
        ref={formRef}
        onSubmit={handleSubmit(async (values: any) => {
          await onSubmit(values);
          reset(values);
        })}
      >
        <TwoColGrid>
          <Controller
            name="costCenter"
            control={control}
            render={({ field }) => (
              <TextInput
                label={t("Common:labels_cost_centre")}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </TwoColGrid>
      </form>
      <Spacer size={16} />
    </>
  );
};

export default ObjectSubscriptionForm;
